// cleanup_remove_external_libraries_PAYMENT_MAPS
// cleanup_convert_to_typescript_file_PAYMENT_MAPS

/* eslint-disable react/jsx-indent */
import React, { useRef, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { camelCase, get, template } from 'lodash';
import { of } from 'rxjs';
import classnames from 'classnames';

import Alert from '@glu/alerts-react';
import { SECONDARY, TERTIARY } from '@glu/buttons-react';
import { ContainerArrowDownNextIcon } from '@glu/icons-react';
import { withFormProvider } from '@glu/validation-react';
import { Dropdown } from '@glu/dropdown-react';

import ThemedLink from 'components/themedLink/ThemedLink';
import { lt } from 'utils/validations/gluValidations';
import { unsubscribeComponent } from 'utils/unsubscribeComponent';
import { ACTIONS, ALERTVARIATIONS, WORKSPACES, RESPONSES } from 'constants.js';
import Button from 'components/gluValidation/Button';
import Checkbox from 'components/checkbox/Checkbox';
import Input from 'components/gluValidation/Input';
import useLocale from 'components/useLocale';
import PlaceResolver, { variants } from 'components/placeResolver/PlaceResolver';
import RadioButton from 'components/radioButton/RadioButton';
import Spinner from 'components/spinner/Spinner';
import {
  getWireTypes,
  getWireImportDefaultFields,
  getDateFormats,
  getSeparators,
  getDelimiters,
  isDuplicateMapName,
  isValidAccountNumber,
  isValidDate,
  isValidBankCode,
  createWireImportMap,
  modifyWireImportMap,
  getEndOfRecordOptions,
  getCharges,
  getWirePreferences
} from 'api/payments/service';
import { retrieveWireImportMap } from 'api/wireImports/service';
import countryCodeListFiles from 'components/widgets/contactCenterListView/PayeeImport/countryCodeList.csv';

import useStyles from './CreateWireImportMap.styles';
import { TRANSCRIPTS_WIRE_TYPES } from '../utils';

const { MODIFY } = ACTIONS;

const COUNTRY_FIELD = 'Beneficiary Account Country';
const COUNTRY_CODE_LIST = 'CountryCodeList.csv';

const {
  SINGLE_CHARACTER: { NO, YES }
} = RESPONSES;

const CreateWireImportMapComponent = ({
  data: formState,
  errors,
  modalRootRef,
  modify,
  onChange: formChange,
  setLeavingPage,
  setLoadingForWiresApi,
  setPaymentType,
  setSending,
  user,
  validate: formValidate,
  wireType
}) => {
  const locale = useLocale();
  const history = useHistory();
  const { location } = history;
  const { id } = useParams();

  const classes = useStyles();

  const [error, setError] = useState(null);
  const [validations, setValidations] = useState({ fields: {} });
  const [apiResponse, setApiResponse] = useState(null);
  const [wirePreferences, setWirePreferences] = useState({});
  const [initial, setInitial] = useState(true);
  const { createdBy } = location?.state || {};
  const prevUrl = location?.state?.previous || WORKSPACES.PYMTCNTR;

  const refs = {
    debitAccountNumber: useRef(),
    beneficiaryBankCode: useRef(),
    sendDate: useRef()
  };

  const [fieldsData, setFieldsData] = useState({
    selectall: {
      trimLeadingBlanks: false,
      trimLeadingZeros: false
    },
    fields: {}
  });

  const [api, setApiState] = useState({
    loading: true
  });
  const [subscriptions, setSubscriptions] = useState({});

  const initialize = () => {
    const { paymentType } = location.state || {};

    // wire type match
    const match = apiResponse.wireTypes.find((t) => t.value === formState.wireType);

    const requests = {
      modify: () =>
        retrieveWireImportMap({
          action: MODIFY,
          mapId: parseInt(id, 10),
          paymentType: initial ? paymentType : match && match.label
        }),
      create: () => getWireImportDefaultFields({ paymentType: formState.wireType })
    };

    const fields$ = modify ? requests.modify() : requests.create();
    const sub$ = fields$.subscribe(
      ({
        data: {
          wireImportMapHeader: header = {},
          wireImportMapDetail = [],
          wireImportMapFields = [],
          isISOFormatEnabled
        }
      }) => {
        const data = modify ? wireImportMapDetail : wireImportMapFields;

        const type = modify
          ? (initial &&
              get(
                apiResponse.wireTypes.find((x) => x.label === header.wireType),
                'value'
              )) ||
            formState.wireType
          : formState.wireType;

        setApiState({
          loading: false,
          type,
          isISOFormatEnabled,
          header,
          fields: data.filter((f) => f.isDisplay === YES),
          requiredFields: data
            .filter((x) => x.required === YES)
            .map((f) => camelCase(f.fieldName)),
          fieldNames: data.filter((x) => x.isDisplay === YES).map((x) => camelCase(x.fieldName))
        });

        setInitial(false);

        Object.assign(formState, {
          skipHeaderRecord: header ? header.skipHeaderRecord : 0,
          skipHeaderRecordValue: header ? header.skipHeaderRecord : 0,
          endOfRecordMethod: get(header, 'endofRecordMethod')
            ? [
                get(apiResponse, 'endOfRecord.source', []).find(
                  (x) => x?.id?.toLowerCase() === header.endofRecordMethod?.toLowerCase()
                )
              ]
            : [get(apiResponse, 'endOfRecord.source.0')]
        });

        let blanks = 0;
        let zeros = 0;
        Object.keys(data).forEach((k) => {
          const field = camelCase(data[k].fieldName);
          const {
            amountDecimal,
            fieldPosition,
            fileSeparator,
            replacementValue,
            specialFormatting,
            trimLeadingBlanks,
            trimLeadingZeros
          } = data.find((x) => camelCase(x.fieldName) === field);

          if (data[k].trimLeadingBlanks > 0) {
            blanks += 1;
          }

          if (data[k].trimLeadingZeros > 0) {
            zeros += 1;
          }

          Object.assign(formState, {
            [`${field}AmountDecimal`]: amountDecimal || '',
            [`${field}FieldPosition`]: fieldPosition || '',
            [`${field}FileSeparatorVisible`]: fileSeparator !== 'no',
            [`${field}ReplacementValue`]:
              field === 'detailOfCharges'
                ? (replacementValue && [
                    apiResponse.charges.source.find((x) => x.id === replacementValue)
                  ]) ||
                  []
                : replacementValue,
            [`${field}TrimLeadingBlanks`]: trimLeadingBlanks,
            [`${field}SpecialFormatting`]: specialFormatting
              ? [
                  apiResponse.dateFormats &&
                    apiResponse.dateFormats.source.find(
                      (x) => x.name.toLowerCase() === specialFormatting.toLowerCase()
                    )
                ]
              : null,
            [`${field}TrimLeadingZeros`]: trimLeadingZeros
          });
        });

        const fieldsCount = data.filter((f) => f.isDisplay === YES).length;

        if (blanks === fieldsCount) {
          Object.assign(formState, {
            trimLeadingBlanks: true
          });
        }

        if (zeros === fieldsCount) {
          Object.assign(formState, {
            trimLeadingZeros: true
          });
        }

        if (modify) {
          Object.assign(formState, {
            mapName: header.mapName,
            delimiter: [apiResponse.delimiters.source.find((x) => x.id === header.delimiter)],
            endOfRecordMethod: [
              get(apiResponse, 'endOfRecord.source', []).find(
                (x) => x.id.toLowerCase() === header.endofRecordMethod.toLowerCase()
              ) || [get(apiResponse, 'endOfRecord.source.0')]
            ].filter(Boolean),
            stripQuotes: header.stripQuotes,
            wireType: type,
            skipHeaderRecord: header.skipHeaderRecord
          });
        }

        formChange('update', true);

        setFieldsData((fieldsData) => ({
          ...fieldsData,
          fields: data
            .map((o) => ({ [camelCase(o.fieldName)]: o }))
            .reduce((acc, o) => ({ ...acc, ...o }))
        }));
      }
    );

    setSubscriptions({ sub$ });
  };

  useEffect(
    () => {
      setLeavingPage(true);

      const wirePrefSubscription = getWirePreferences().subscribe((data) => {
        setWirePreferences(data);
      });

      setSubscriptions({ wirePrefSubscription });

      return () => {
        setLeavingPage(false);
        unsubscribeComponent(subscriptions);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!formState.wireType) {
      return;
    }

    if (formState.wireType !== api.type) {
      setApiState((state) => ({ ...state, loading: true }));
      initialize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.wireType]);

  useEffect(() => {
    if (!formState.sendDateSpecialFormatting) {
      return;
    }

    const sendDateField = refs.sendDate && refs.sendDate.current;
    const sendDateReplacementValue = formState.sendDateReplacementValue;

    if (sendDateField && sendDateReplacementValue) {
      sendDateField.focus();
      sendDateField.blur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.sendDateSpecialFormatting]);

  const validateSingleField = ({ decimal, fieldName, flags = false, instance, name, value }) => {
    const field = name.replace(new RegExp(`^${fieldName}`, 'g'), '');
    const val = decimal && value ? parseInt(value, 10) : value;
    const isFieldPosition = name.includes('FIeldPosition');

    const duplicate = () => {
      const values = Object.keys(formState)
        .map((f) => {
          const match = f.match(new RegExp(`[a-zA-Z0-9]+${field}$`, 'g'));
          return match ? formState[f] : null;
        })
        .filter(Boolean);

      // checking for multiple duplicates in a tree
      const overwrite = Object.keys(formState)
        .map((f) => {
          const match = f.match(new RegExp(`[a-zA-Z0-9]+${field}$`, 'g'));
          if (!match || !formState[f]) {
            return null;
          }

          return {
            [f]: {
              ...get(validations, `fields.${f}`, []),
              duplicate: !isFieldPosition
                ? false
                : values.filter((v) => v === formState[f]).length > 1
            }
          };
        })
        .filter(Boolean)
        .reduce((acc, o) => ({ ...acc, ...o }), {});

      const isApplied = formState[name] === val;
      const duplicate =
        val === ''
          ? false
          : !(values.filter((x) => x === val).length < (!isApplied ? 1 : 2) && val > 0);

      if (flags) {
        return duplicate;
      }

      setValidations((validations) => ({
        ...validations,
        fields: {
          ...validations.fields,
          ...overwrite,
          [name]: {
            ...((validations.fields && validations.fields[name]) || []),
            duplicate: !isFieldPosition ? false : duplicate
          }
        }
      }));

      return false;
    };

    const required = () => {
      const checkRequired = () => {
        if (instance) {
          return instance.required === YES && val === '';
        }
        return val === '';
      };

      const isRequired = checkRequired();
      const bindings = {
        FieldPosition: 'ReplacementValue',
        ReplacementValue: 'FieldPosition'
      };

      const opposite = `${camelCase(fieldName)}${bindings[field]}`;

      if (flags) {
        return isRequired;
      }

      setValidations((validations) => ({
        ...validations,
        fields: {
          ...validations.fields,
          [name]: {
            ...((validations.fields && validations.fields[name]) || []),
            required: formState[opposite] ? false : isRequired
          },
          [opposite]: {
            ...((validations.fields && validations.fields[opposite]) || []),
            required: !val
              ? get(validations, `fields.${opposite}.required`) && !(val && isRequired)
              : isRequired
          }
        }
      }));

      return false;
    };

    // validations based on type
    const validate = {
      duplicate,
      required
    };

    const isDuplicate = validate.duplicate();
    const isRequired = validate.required();

    if (flags) {
      return { duplicate: isDuplicate, required: isRequired };
    }

    return false;
  };

  const validateTableFields = () => {
    // internal validation
    const validationState = api.requiredFields
      .map((field) => {
        const { duplicate, required } = validateSingleField({
          name: `${field}FieldPosition`,
          value: get(formState, `${field}FieldPosition`, false),
          fieldName: field,
          flags: true
        });

        const replacementValue = get(formState, `${field}ReplacementValue`, false);

        return {
          [`${field}FieldPosition`]: {
            duplicate,
            required: replacementValue ? false : required
          },
          [`${field}ReplacementValue`]: {
            ...get(validations, `fields.${field}ReplacementValue`, []),
            duplicate: false,
            required: false
          }
        };
      })
      .reduce((acc, o) => ({ ...acc, ...o }), {});

    const invalid =
      Object.keys(validationState)
        .map((field) => validationState[field].duplicate || validationState[field].required)
        .includes(true) ||
      Object.values(errors).filter((error) => {
        if (error === undefined) return false;
        return Array.isArray(error).length === 0;
      }).length > 0;

    setValidations((validations) => ({
      ...validations,
      fields: {
        ...validations.fields,
        ...validationState
      }
    }));

    return { invalid };
  };

  const onChange = (type) => (data, value, add) => {
    const events = {
      radio: ({ name, value }) => {
        formChange(name, value);
      },
      checkbox: (e) => {
        const target = e.currentTarget;
        const checked = target.checked;
        const yesno = checked ? YES : NO;

        formChange(target.id, value && value.yesno ? yesno : +checked);

        if (target.id === 'skipHeaderRecord') {
          formChange('skipHeaderRecordValue', +checked);
        }
      },
      dropdown: (name, value) => {
        const fields = ['endOfRecordMethod', 'delimiter'];
        const isDelimiterOrEndOfRecord = ['endOfRecordMethod', 'delimiter'].includes(name);

        if (isDelimiterOrEndOfRecord) {
          const opposite = name === 'endOfRecordMethod' ? fields[1] : fields[0];
          const values = {
            opposite: get(formState, `${opposite}.0.id`),
            current: get(value, '0.id')
          };

          if (values.opposite !== values.current) {
            formValidate(opposite);
          }
        } else {
          formChange(name, value);
        }
      },
      input: (name, value) => {
        if (name === 'skipHeaderRecordValue') {
          formChange('skipHeaderRecord', +value);
        }

        formChange(name, value);
      },
      tableInput: ({ name, value }, field, decimal = false) => {
        const instance = api.fields.find((x) => camelCase(x.fieldName) === field);

        validateSingleField({
          name,
          value,
          fieldName: field,
          decimal,
          instance
        });
        formChange(name, decimal ? (value && parseInt(value, 10)) || '' : value);
      },
      tableCheckbox: (e) => {
        const target = e.target;
        const selectAll = target.dataset.selectall;
        const value = target.dataset.checkvalue;
        const isZero = target.id.match(/[a-zA-Z]+Zeros$/g);

        if (!selectAll) {
          const hasWord = typeof value === 'string';
          const wording = value === YES ? NO : YES;
          const addSeparator = /([a-zA-Z]+)FileSeparatorVisible/g.exec(target.id);

          if (addSeparator) {
            const separators = apiResponse.separators.source;
            const instance = api.fields.find((x) => camelCase(x.fieldName) === 'sendDate');
            const selected = separators.find((s) => s.id === instance.fileSeparator);
            let value = separators[0];

            if (selected) {
              value = [selected];
            }

            formChange(`${addSeparator[1]}FileSeparator`, target.checked ? value : 'no');
          }

          formChange(target.id, hasWord ? wording : +target.checked);

          if (!hasWord) {
            let count = 0;
            Object.keys(formState).forEach((name) => {
              const match = name.match(
                new RegExp(`[a-zA-Z0-9]+TrimLeading(${isZero ? 'Zeros' : 'Blanks'})`, 'g')
              );
              if (match) {
                const checked = formState[name] >= 1;
                if (checked) {
                  count += 1;
                }
              }
            });

            if (count === api.fields.length) {
              formChange(`trimLeading${isZero ? 'Zeros' : 'Blanks'}`, false);
            }

            if (count + 1 === api.fields.length && target.checked) {
              formChange(`trimLeading${isZero ? 'Zeros' : 'Blanks'}`, true);
            }
          }
          return;
        }

        formChange(target.id, target.checked);
        api.fieldNames.forEach((name) => {
          formChange(`${name}TrimLeading${isZero ? 'Zeros' : 'Blanks'}`, +target.checked);
        });
      }
    };

    // in case type is not supported
    if (!events[type]) {
      return;
    }

    events[type](data, value, add);
  };

  const getProperReplacementField = ({ charges, f }) => {
    getProperReplacementField.propTypes = {
      charges: PropTypes.shape({}).isRequired,
      f: PropTypes.string.isRequired
    };

    const isCharges = f === 'detailOfCharges';
    const isTransactionCurrency = f === 'transactionCurrency';

    const onBlurValidations = {
      debitAccountNumber: [
        {
          validate: ({ serviceData: { name, valid, value } }) => {
            setValidations(() => ({
              ...validations,
              fields: {
                ...validations.fields,
                [name]: {
                  ...(validations.fields && validations.fields[name]),
                  value,
                  failed: value.length > 0 && !valid
                }
              }
            }));

            return !value || valid;
          },
          service: ({ name, value }) => {
            const validation = validations.fields[name];
            if (!value && !validation?.failed) {
              return false;
            }

            return isValidAccountNumber({ name, accountNumber: value });
          },
          msg: locale['wireImports.mapErrors.debitAccountNumber']
        }
      ],
      beneficiaryBankCode: [
        {
          validate: ({ serviceData: { name, valid, value } }) => {
            setValidations(() => ({
              ...validations,
              fields: {
                ...validations.fields,
                [name]: {
                  ...(validations.fields && validations.fields[name]),
                  value,
                  failed: value && !valid
                }
              }
            }));

            return !value || valid;
          },
          service: ({ name, value }) => {
            const validation = validations.fields[name];
            if (!value && !validation?.failed) {
              return false;
            }

            return isValidBankCode({ name, beneficiaryBankCode: value });
          },
          msg: locale['wireImports.mapErrors.beneficiaryBankCode']
        }
      ],
      sendDate: [
        {
          validate: ({ serviceData: { name, valid, value } }) => {
            const bool = typeof valid === 'boolean';

            setValidations(() => ({
              ...validations,
              fields: {
                ...validations.fields,
                [name]: {
                  ...(validations.fields && validations.fields[name]),
                  value,
                  failed: value.length > 0 && ((bool && !valid) || !bool)
                }
              }
            }));

            return !value || (bool && valid);
          },
          service: ({ name, value }) => {
            const validation = validations.fields[name];
            if (!value && !validation.failed) {
              return true;
            }

            return isValidDate({
              name,
              dateFormat: get(formState, 'sendDateSpecialFormatting.0.name'),
              date: value
            });
          },
          msg: locale['wireImports.mapErrors.sendDate']
        }
      ]
    };

    if (isCharges) {
      return (
        <Dropdown
          className={classes.tableDropdown}
          htmlId={`${f}ReplacementValue`}
          name={`${f}ReplacementValue`}
          options={charges.source}
          onChange={onChange('dropdown')}
          defaultValue={formState[`${f}ReplacementValue`]}
          filterEnabled={false}
        />
      );
    }

    const field = api.fields.find((x) => camelCase(x.fieldName) === f);

    const showCharCount = field && field.size && !isTransactionCurrency;
    return (
      <Input
        className={showCharCount && classes.inputPadding}
        disabled={isTransactionCurrency}
        name={`${f}ReplacementValue`}
        value={formState[f]}
        onChange={(name, value) => onChange('tableInput')({ name, value }, f)}
        onBlurValidations={onBlurValidations[f] || []}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(showCharCount
          ? {
              charCount: `${(formState[`${f}ReplacementValue`] || '').length}/${field.size}`,
              validators: [
                lt({
                  maxLength: field.size,
                  msg: template(locale['validation.maxNCharacters'])({ max: field.size })
                })
              ]
            }
          : [])}
        forwardedRef={refs[f]}
      />
    );
  };

  const onValidate = () => {
    const { invalid } = validateTableFields();

    // glu validation
    formValidate();

    if (invalid) {
      setError(locale['validation.required']);

      // eslint-disable-next-line no-param-reassign
      modalRootRef.scrollTop = 0;
      return null;
    }
    setSending(true);
    return null;
  };

  const onClear = ({ charges, dateFormats, delimiters, endOfRecord, separators }) => {
    Object.keys(api.fields).forEach((k) => {
      const field = camelCase(api.fields[k].fieldName);
      const {
        amountDecimal,
        fieldPosition,
        fileSeparator,
        replacementValue,
        trimLeadingBlanks,
        trimLeadingZeros
      } = api.fields.find((x) => camelCase(x.fieldName) === field);

      const separator = get(fieldsData, `fields.${field}.fileSeparator`, false);
      const special = get(fieldsData, `fields.${field}.specialFormatting`, false);

      const getReplacementValue = () => {
        const isCharges = field === 'detailOfCharges';

        if (isCharges) {
          return replacementValue
            ? [charges.source.find((x) => x.id === replacementValue)]
            : charges[0];
        }

        return replacementValue;
      };

      Object.assign(formState, {
        [`${field}AmountDecimal`]: amountDecimal,
        [`${field}FieldPosition`]: fieldPosition || '',
        [`${field}TrimLeadingBlanks`]: trimLeadingBlanks,
        [`${field}TrimLeadingZeros`]: trimLeadingZeros,
        [`${field}FileSeparatorVisible`]: !!(fileSeparator && fileSeparator !== 'no'),
        trimLeadingZeros: 0,
        trimLeadingBlanks: 0,
        delimiter: [delimiters.source[0]],
        mapName: get(api.header, 'mapName', ''),
        skipHeaderRecord: 0,
        stripQuotes: 0,
        endOfRecordMethod: [endOfRecord.source[0]],
        [`${field}ReplacementValue`]: getReplacementValue() || '',
        [`${field}FileSeparator`]: separator
          ? separators.source.find((x) => x.name.toLowerCase() === separator.toLowerCase())
          : [],
        [`${field}SpecialFormatting`]: special
          ? [dateFormats.source.find((x) => x.name.toLowerCase() === special.toLowerCase())]
          : []
      });
    });

    setError(null);
    setValidations(() => ({ fields: {} }));
    formChange('clear', true);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const failing =
      Object.keys(validations.fields)
        .map(
          (field) =>
            validations.fields[field].duplicate ||
            validations.fields[field].required ||
            validations.fields[field].failed
        )
        .includes(true) ||
      Object.values(errors).filter((error) => {
        if (error === undefined) return false;
        return Array.isArray(error).length === 0;
      }).length > 0;

    if (failing) return false;

    const payload = {
      wireImportMapDetail: [
        ...api.fields.map((f) => {
          const field = camelCase(f.fieldName);
          const replacementValue = formState[`${field}ReplacementValue`] || null;
          const isCharges = field === 'detailOfCharges';

          return {
            ...f,
            amountDecimal: formState[`${field}AmountDecimal`] || null,
            fieldPosition: (!replacementValue && formState[`${field}FieldPosition`]) || null,
            fileSeparator:
              get(formState[`${field}FileSeparator`], '0.name', '').toLowerCase() || 'no',
            replacementValue: isCharges ? get(replacementValue, '0.id') : replacementValue,
            specialFormatting: get(formState[`${field}SpecialFormatting`], '0.name') || null,
            trimLeadingBlanks: formState[`${field}TrimLeadingBlanks`],
            trimLeadingZeros: formState[`${field}TrimLeadingZeros`]
          };
        })
      ],
      wireImportMapHeader: {
        createdBy: modify ? createdBy : user.userCode || null,
        delimiter: get(formState.delimiter, '0.id'),
        endofRecordMethod: get(formState, 'endOfRecordMethod.0.id', ''),
        fileType: 'Delimited',
        mapName: formState.mapName,
        mapType: 'Payment',
        skipHeaderRecord: formState.skipHeaderRecord || 0,
        stripQuotes: formState.stripQuotes || NO,
        templateType: 'Custom',
        wireType: formState.wireType,
        ...(modify ? { mtMapId: parseInt(id, 10) } : [])
      }
    };

    const api$ = modify ? modifyWireImportMap(payload) : createWireImportMap(payload);
    return api$.subscribe(({ data, success }) => {
      if (!success) {
        const message = get(data, '0.userMessage');
        const code = get(data, '0.code');

        const mappings = {
          'PAYMENTS.INVALID_MAP_NAME': locale['wireImports.apiErrors.invalidMapName'],
          'PAYMENTS.INVALID_REPLACEMENT_VALUE':
            locale['wireImports.apiErrors.noSpecialCharacters'],
          'PAYMENTS.INVALID_FIRST_CHARACTER ': locale['wireImports.apiErrors.removeColonOrHyphen']
        };

        if (message) {
          const pattern = /\\n(.+?)\\n/g;
          const match = pattern.exec(message);
          const field = match && api.fields.find((f) => f.fieldName === match[1]);

          setError({
            message: mappings[code] ? mappings[code] : message,
            field: field && field.displayName
          });
        } else {
          setError(locale['wireImports.mapErrors.default']);
        }

        // eslint-disable-next-line no-param-reassign
        modalRootRef.scrollTop = 0;
        return;
      }

      history.push({
        pathname: prevUrl,
        state: {
          messages: [
            {
              id: 1,
              modifyOrCreate: true,
              title: success
                ? locale[
                    modify ? 'wireImports.mapHasBeenModified' : 'wireImports.mapHasBeenCreated'
                  ]
                : locale[
                    modify
                      ? 'wireImports.mapHasFailedToModify'
                      : 'wireImports.mapHasFailedToCreate'
                  ],
              data: { mapName: formState.mapName },
              success
            }
          ]
        }
      });
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const services = useMemo(() => [
    getWireTypes,
    getDelimiters,
    getSeparators,
    getDateFormats,
    getEndOfRecordOptions,
    getCharges
  ]);

  // For CreateImportMap Component to understand what paymentType is set (for rendering header text)
  useEffect(() => {
    if (modify) {
      setPaymentType(
        Object.keys(TRANSCRIPTS_WIRE_TYPES).filter(
          (key) => TRANSCRIPTS_WIRE_TYPES[key] === formState.wireType
        )
      );
    }
  }, [modify, setPaymentType, formState.wireType]);

  // For CreateImportMap Component to understand when WIRE'S API is done
  useEffect(() => setLoadingForWiresApi(api.loading), [setLoadingForWiresApi, api.loading]);
  return (
    <form className={classes.wrapper} onSubmit={onSubmit}>
      <header className={classes.header}>
        <h4 className={classes.subtitle}>{locale['wireImports.delimitedFileMap']}</h4>
      </header>

      {error && (
        <Alert
          dataQa="createWireImport-error"
          className={classes.alert}
          variant={ALERTVARIATIONS.ERROR}
          onHide={() => setError(null)}
          title={
            modify ? locale['wireImports.canNotModifyMap'] : locale['wireImports.canNotCreateMap']
          }
          message={
            error.message && (
              <div>
                <div>
                  {error.message}
                  {error.field ? ' in ' : ''}
                </div>
                {error.field && <div>{`'${error.field}'`}</div>}
              </div>
            )
          }
        />
      )}

      {modify && wirePreferences?.isoFormatEnabled && !api?.isISOFormatEnabled && (
        <Alert
          dataQa="createWireImport-iso-address-warning"
          variant={ALERTVARIATIONS.WARNING}
          className={classes.alert}
          message={locale['wireImports.updateIsoAddress']}
        />
      )}

      <PlaceResolver services={services} variant={variants.table}>
        {({
          results: [
            { radio },
            { dropdown: delimiters },
            { dropdown: separators },
            { dropdown: dateFormats },
            { dropdown: endOfRecord },
            { dropdown: charges }
          ]
        }) => {
          const separatorsDetail = separators.source.filter((x) => x.id !== 'NO');

          if (!apiResponse) {
            setApiResponse({
              wireTypes: radio.source,
              delimiters,
              separators,
              endOfRecord,
              dateFormats,
              charges
            });
          }

          return (
            <>
              <section className={classes.optionsSection}>
                {/* Wire types */}
                <RadioButton
                  className={classes.hidden}
                  style
                  name="wireType"
                  value={!modify ? formState.wireType || wireType : radio.preselected}
                  data={radio.source}
                  settings={{
                    titleOnTop: true
                  }}
                  title={locale['wireImports.selectPaymentType']}
                  onUpdate={(data, value, add) => {
                    if (!initial) {
                      setValidations(() => ({ fields: {} }));
                      onClear({
                        delimiters,
                        endOfRecord,
                        dateFormats,
                        charges,
                        separators
                      });
                    }
                    onChange('radio')(data, value, add);
                  }}
                  styled
                />

                {/* Map name */}
                <Input
                  className={classnames('form-group', classes.mapName)}
                  name="mapName"
                  data-qa="mapName"
                  label={locale['wireImports.mapName']}
                  charCount={`${(formState.mapName || '').length}/25`}
                  value={formState.mapName}
                  validators={[
                    { required: true, msg: locale['validation.required'] },
                    lt({
                      maxLength: 25,
                      msg: template(locale['validation.maxNCharacters'])({ max: 25 })
                    })
                  ]}
                  onBlurValidations={[
                    {
                      validate: ({ serviceData: { duplicate, name, value } }) => {
                        setValidations(() => ({
                          ...validations,
                          fields: {
                            ...validations.fields,
                            [name]: {
                              ...get(validations, `fields.${name}`, []),
                              value,
                              failed: duplicate
                            }
                          }
                        }));

                        return !duplicate;
                      },
                      service: ({ value }) => {
                        if (get(api, 'header.mapName') === value) {
                          return of(true);
                        }

                        return isDuplicateMapName({ mapName: value });
                      },
                      msg: locale['wireImports.mapNameAlreadyUsed']
                    }
                  ]}
                  onChange={onChange('input')}
                />

                {/* Delimiter */}
                <Dropdown
                  className={classes.delimiters}
                  htmlId="delimiter"
                  name="delimiter"
                  labelText={locale['common.delimiter']}
                  dataQa="map-delimiter"
                  options={delimiters.source}
                  onChange={onChange('dropdown')}
                  filterEnabled={false}
                  value={formState.delimiter}
                  defaultValue={[delimiters.source[0]]}
                  validators={[
                    { required: true, msg: locale['validation.required'] },
                    {
                      fn: (name, param, data) => {
                        const currValue = get(data, `${name}.0.id`);
                        const endOfRecordValue = get(data, 'endOfRecordMethod.0.id');
                        return currValue !== endOfRecordValue;
                      },
                      msg: locale['wireImports.mapErrors.DELIMITER_AND_END_OF_RECORD_MISMATCH']
                    }
                  ]}
                />

                <Dropdown
                  className={classes.delimiters}
                  htmlId="endOfRecordMethod"
                  name="endOfRecordMethod"
                  options={endOfRecord.source}
                  labelText={locale['wireImports.endOfRecordMethod']}
                  dataQa="map-eorMethod"
                  onChange={onChange('dropdown')}
                  validators={[
                    { required: true, msg: locale['validation.required'] },
                    {
                      fn: (name, param, data) => {
                        const currValue = get(data, `${name}.0.id`);
                        const delimiterValue = get(data, 'delimiter.0.id');
                        return currValue !== delimiterValue;
                      },
                      msg: locale['wireImports.mapErrors.DELIMITER_AND_END_OF_RECORD_MISMATCH']
                    }
                  ]}
                  defaultValue={[endOfRecord.source[0]]}
                  filterEnabled={false}
                />

                {/* Options */}
                <div className={classes.extraOptions}>
                  <div>
                    <Checkbox
                      label={locale['wireImports.skipHeaderRecords']}
                      id="skipHeaderRecord"
                      checked={formState.skipHeaderRecord}
                      className={classes.tableCheckbox}
                      onChange={onChange('checkbox')}
                    />

                    {formState.skipHeaderRecord > 0 && (
                      <Input
                        className={classes.countInput}
                        label={locale['wireImports.count']}
                        name="skipHeaderRecordValue"
                        type="number"
                        validators={[
                          { required: true, msg: locale['validation.required'] },
                          {
                            fn: (name, param, data) => Number(data[name]) >= 1,
                            msg: template(locale['common.greaterThan'])({ field: 0 })
                          }
                        ]}
                        onChange={onChange('input')}
                        value={formState.skipHeaderRecordValue}
                      />
                    )}
                  </div>
                  <div>
                    <Checkbox
                      label={locale['wireImports.stripQuotesAroundFields']}
                      id="stripQuotes"
                      checked={formState.stripQuotes === YES}
                      onChange={(e) => onChange('checkbox')(e, { yesno: true })}
                      className={classes.tableCheckbox}
                    />
                  </div>
                </div>
              </section>
              <section className={classes.tableContent}>
                {api.loading && (
                  <div className={classes.spinner}>
                    <Spinner />
                  </div>
                )}

                {!api.loading && (
                  <table className={classes.table}>
                    <thead className={classes.tableThead}>
                      <tr className={classes.columnGroupHeading}>
                        <th />
                        <th colSpan="2">{locale['wireImports.groupTitle.trimLeading']}</th>
                      </tr>
                      <tr className={classes.tableHeading}>
                        <th>{locale['importMapping.title.fieldName']}</th>
                        <th>{locale['wireImports.title.blanks']}</th>
                        <th>{locale['wireImports.title.zeros']}</th>
                        <th>{locale['importMapping.title.specialFormatting']}</th>
                        <th>{locale['importMapping.title.fieldPosition']}</th>
                        <th className={classes.replacementValue}>
                          {locale['importMapping.title.replacementValue']}
                        </th>
                      </tr>
                      <tr className={classes.headingActions}>
                        <th>{locale['common.selectAll']}</th>
                        <th>
                          <Checkbox
                            id="trimLeadingBlanks"
                            onChange={onChange('tableCheckbox')}
                            data-selectall="trimLeadingBlanks"
                            checked={formState.trimLeadingBlanks}
                          />
                        </th>
                        <th>
                          <Checkbox
                            id="trimLeadingZeros"
                            onChange={onChange('tableCheckbox')}
                            data-selectall="zeros"
                            checked={formState.trimLeadingZeros}
                          />
                        </th>
                        <th />
                        <th colSpan="2" className={classes.tableHeadingDesc}>
                          {locale['wireImports.messageRow.message']}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(fieldsData.fields).map((f) => {
                        const field = fieldsData.fields[f];
                        const visible = field.isDisplay === YES;
                        const duplicate = get(
                          validations,
                          `fields.${camelCase(field.fieldName)}FieldPosition.duplicate`,
                          false
                        );

                        const required = {
                          position: get(
                            validations,
                            `fields.${camelCase(field.fieldName)}FieldPosition.required`,
                            false
                          ),
                          replacement: get(
                            validations,
                            `fields.${camelCase(field.fieldName)}ReplacementValue.required`,
                            false
                          )
                        };

                        if (!visible) {
                          return null;
                        }

                        return (
                          <tr
                            key={`wireImportField-${field.fieldName}`}
                            data-qa={`field-row-${field.fieldName
                              ?.replace(/\s/g, '-')
                              .toLowerCase()}`}
                          >
                            <td
                              className={classnames({
                                [classes.tableFieldOptional]: field.required !== YES
                              })}
                            >
                              {`${field.displayName} ${field.size !== 0 ? `(${field.size})` : ''}`}
                              {field.fieldName === COUNTRY_FIELD && (
                                <ThemedLink
                                  className={classes.countryCodeDownloadBtn}
                                  data-qa="download-country-code-list"
                                  title={locale['payeeImport.viewDetails.countryCodeList']}
                                  bare
                                  to={countryCodeListFiles}
                                  download={COUNTRY_CODE_LIST}
                                >
                                  <ContainerArrowDownNextIcon />
                                  {locale['payeeImport.viewDetails.countryCodeList']}
                                </ThemedLink>
                              )}
                            </td>
                            <td>
                              <Checkbox
                                id={`${f}TrimLeadingBlanks`}
                                className={classes.tableCheckbox}
                                checked={!!formState[`${f}TrimLeadingBlanks`]}
                                onClick={(e) =>
                                  onChange('tableCheckbox')(e, camelCase(field.fieldName))
                                }
                              />
                            </td>
                            <td>
                              <Checkbox
                                id={`${f}TrimLeadingZeros`}
                                className={classes.tableCheckbox}
                                checked={!!formState[`${f}TrimLeadingZeros`]}
                                onClick={(e) =>
                                  onChange('tableCheckbox')(e, camelCase(field.fieldName))
                                }
                              />
                            </td>
                            <td>
                              {field.amountDecimal && (
                                <Checkbox
                                  id={`${f}AmountDecimal`}
                                  className={classes.tableCheckbox}
                                  onClick={(e) =>
                                    onChange('tableCheckbox')(e, camelCase(field.fieldName))
                                  }
                                  data-checkvalue={formState[`${f}AmountDecimal`]}
                                  checked={formState[`${f}AmountDecimal`] === YES}
                                  label={locale['wireImports.useDecimal']}
                                />
                              )}

                              {field.specialFormatting && (
                                <div>
                                  <Dropdown
                                    className={classes.tableDropdown}
                                    htmlId={`${f}SpecialFormatting`}
                                    name={`${f}SpecialFormatting`}
                                    options={dateFormats.source}
                                    onChange={onChange('dropdown')}
                                    defaultValue={formState[`${f}SpecialFormatting`]}
                                    filterEnabled={false}
                                  />
                                </div>
                              )}

                              {field.fileSeparator && (
                                <div>
                                  <Checkbox
                                    id={`${f}FileSeparatorVisible`}
                                    className={classes.tableCheckbox}
                                    onClick={(e) =>
                                      onChange('tableCheckbox')(e, camelCase(field.fieldName))
                                    }
                                    checked={
                                      formState[`${f}FileSeparatorVisible`] &&
                                      formState[`${f}FileSeparatorVisible`]
                                    }
                                    label={locale['wireImports.addSeparator']}
                                  />

                                  {formState[`${f}FileSeparatorVisible`] > 0 && (
                                    <Dropdown
                                      className={classes.tableDropdown}
                                      htmlId={`${f}FileSeparator`}
                                      name={`${f}FileSeparator`}
                                      options={separatorsDetail}
                                      onChange={onChange('dropdown')}
                                      defaultValue={[
                                        separatorsDetail.find(
                                          (x) =>
                                            x.name.toLowerCase() ===
                                            get(
                                              fieldsData,
                                              `fields.${f}.fileSeparator`
                                            ).toLowerCase()
                                        ) || separatorsDetail[0]
                                      ]}
                                      filterEnabled={false}
                                    />
                                  )}
                                </div>
                              )}
                            </td>
                            <td
                              className={classnames(classes.fieldPosition, classes.tableInput, {
                                [classes.hasError]: required.position || duplicate
                              })}
                            >
                              <Input
                                name={`${f}FieldPosition`}
                                type="number"
                                min="1"
                                value={formState[f]}
                                onChange={(name, value) =>
                                  onChange('tableInput')(
                                    { name, value },
                                    camelCase(field.fieldName),
                                    true
                                  )
                                }
                              />
                              <span>
                                {get(validations, `fields.${f}FieldPosition.required`, false)
                                  ? locale['validation.required']
                                  : locale['validation.duplicateField']}
                              </span>
                            </td>
                            <td
                              className={classnames(classes.tableInput, classes.replacementValue, {
                                [classes.hasError]: required.replacement
                              })}
                            >
                              {field.isReplacementValue === YES && (
                                <>
                                  {getProperReplacementField({ charges, f })}
                                  <span
                                    className={
                                      !['detailOfCharges', 'transactionCurrency'].includes(f) &&
                                      classes.additionalValidation
                                    }
                                  >
                                    {!get(validations, `fields.${f}ReplacementValue.failed`) &&
                                      get(validations, `fields.${f}ReplacementValue.required`) &&
                                      locale['validation.required']}
                                  </span>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </section>

              {!api.loading && (
                <section className={classes.footer}>
                  <Button
                    dataQa="save-button"
                    type="submit"
                    className="btn btn-primary"
                    onClick={onValidate}
                  >
                    {locale['common.save']}
                  </Button>

                  <Button
                    className="btn"
                    onClick={() => history.push({ pathname: prevUrl })}
                    variant={SECONDARY}
                  >
                    {locale['common.cancel']}
                  </Button>

                  <Button
                    onClick={() =>
                      onClear({
                        delimiters,
                        endOfRecord,
                        dateFormats,
                        charges,
                        separators
                      })
                    }
                    variant={TERTIARY}
                    data-qa="clear-button"
                  >
                    {locale['common.clear']}
                  </Button>
                </section>
              )}
            </>
          );
        }}
      </PlaceResolver>
    </form>
  );
};

CreateWireImportMapComponent.propTypes = {
  errors: PropTypes.shape({}).isRequired,
  modalRootRef: PropTypes.node.isRequired,
  validate: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  setSending: PropTypes.func,
  modify: PropTypes.bool,
  user: PropTypes.shape({}).isRequired,
  wireType: PropTypes.string.isRequired,
  setPaymentType: PropTypes.func,
  setLoadingForWiresApi: PropTypes.func,
  setLeavingPage: PropTypes.func
};

CreateWireImportMapComponent.defaultProps = {
  setLeavingPage() {},
  modify: false,
  setSending() {},
  setPaymentType() {},
  setLoadingForWiresApi() {}
};

/* istanbul ignore next */
const mapStateToProps = (state) => ({
  user: state.auth.user
});

export default connect(mapStateToProps, null)(withFormProvider(CreateWireImportMapComponent));
